import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { usePermissionsContext } from "../../context/permissionContext";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { Item, KitComponent } from "../../http/item/types";
import useOrganizationApi from "../../http/organization/api";
import { Organization } from "../../http/organization/types";
import { formatPrice, getItemDescription } from "../../utils";
import Button from "../ui/Button";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import Input from "../ui/Input";
import LoadingSpinner from "../ui/LoadingSpinner";
import reactSelectStyles from "../ui/ReactSelectUtils";
import Table from "../ui/Table";
import styles from "./ItemForm.module.css";

export interface ItemFormBody {
  organization: Organization | null;
  item: Item | null;
}

type ItemFormProps = {
  item?: Item;
  isDisabled?: boolean;
  isLoading?: boolean;
  onSubmit?: (data: ItemFormBody) => void;
  onDelete?: () => void;
  onSync?: () => void;
};

const ItemForm: FC<ItemFormProps> = ({ item, isDisabled, isLoading, onSubmit, onDelete, onSync }) => {
  const i18n = useI18n();
  const organizationsApi = useOrganizationApi();
  const dispatch = useContext(NotificationDispatch);
  const [defaultItemsOptions, setDefaultItemsOptions] = useState<Item[]>([]);
  const [defaultOrganizationOptions, setDefaultOrganizationOptions] = useState<Organization[]>([]);
  const permissions = usePermissionsContext();
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState<boolean>(true);
  const [isLoadingComponents, setIsLoadingComponents] = useState<boolean>(false);

  const defaultValues = {
    item: {
      code: item?.code ?? "",
      cost: item?.cost ?? 0,
      descriptionEn: item?.descriptionEn ?? "",
      descriptionIt: item?.descriptionIt ?? "",
      descriptionEs: item?.descriptionEs ?? "",
      descriptionFr: item?.descriptionFr ?? "",
      descriptionDe: item?.descriptionDe ?? "",
      isKit: item?.isKit ?? false,
      kitComponents: item?.kitComponents ?? [],
      costObject: item?.costObject ?? "",
      factor: item?.factor ?? 0,
      unitOfMeasure: item?.unitOfMeasure || i18n.t.common.pz,
    },
  };

  return (
    <Form
      submitText={i18n.t.common.save}
      onSubmit={
        onSubmit && (permissions.isWarehouseOperator || permissions.isPurchaseOffice) && !item
          ? () => onSubmit
          : undefined
      }
      defaultValues={defaultValues}
      disabled={!!item || isLoadingComponents || isLoading || isDisabled}
      secondaryButton={
        permissions.isWarehouseOperator || permissions.isPurchaseOffice ? (
          <>
            {!(onSubmit && (permissions.isWarehouseOperator || permissions.isPurchaseOffice) && !item) &&
              !!isLoading && <LoadingSpinner />}
            {onDelete && <Button buttonProps={{ onClick: onDelete }}>{i18n.t.common.delete}</Button>}
            {onSync && <Button buttonProps={{ onClick: onSync }}>{i18n.t.common.sync}</Button>}
          </>
        ) : undefined
      }
    >
      {({ control, setValue, watch, reset }) => {
        const kitComponents = watch("item")?.kitComponents ?? [];
        const organization = watch("organization");

        useEffect(() => {
          reset(defaultValues);
        }, [item]);

        return (
          <>
            {!item && (
              <Controller
                name="organization"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.organization} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef<AbortController | undefined>(undefined);
                      const loadOptions = createLoadOptions<Organization>(
                        aborter,
                        searchTerm,
                        organizationsApi.searchOrganizations,
                      );

                      useEffect(() => {
                        // Try to fetch and set IT001 as default if present
                        loadOptions("IT001")
                          .then((options) => {
                            setDefaultOrganizationOptions(options);
                            const it001 = options.find((o) => o.code?.toUpperCase() === "IT001");
                            it001 && setValue("organization", it001);
                          })
                          .catch((err) => dispatch(showErrorNotification(i18n, err)))
                          .finally(() => setIsLoadingOrganizations(false));
                      }, []);

                      return (
                        <AsyncSelect<Organization>
                          {...field}
                          cacheOptions
                          styles={reactSelectStyles(isInvalid)}
                          loadOptions={(st) => loadOptions(st)}
                          defaultOptions={defaultOrganizationOptions}
                          id={labelId}
                          isLoading={isLoading || isLoadingOrganizations}
                          isDisabled
                          getOptionLabel={(o) => `${o.code}`}
                          getOptionValue={(o) => o.id}
                          placeholder={i18n.t.common.select}
                        />
                      );
                    }}
                  </FormField>
                )}
              />
            )}

            {!item && (
              <Controller
                name="item.code"
                control={control}
                rules={{ required: true }}
                render={({ fieldState }) => (
                  <FormField label={i18n.t.common.code} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef<AbortController | undefined>(undefined);
                      const loadOptions = createLoadOptions<Item>(aborter, searchTerm, (query) =>
                        organization ? organizationsApi.searchNewItems(query, organization.id) : Promise.resolve([]),
                      );

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultItemsOptions);
                      }, [organization]);

                      return (
                        <AsyncSelect<Item>
                          inputId={labelId}
                          defaultOptions={defaultItemsOptions}
                          loadOptions={(st) => loadOptions(st)}
                          getOptionLabel={(o) => o.code}
                          getOptionValue={(o) => o.id}
                          placeholder={i18n.t.common.select + "..."}
                          isLoading={isLoadingComponents}
                          isDisabled={isDisabled || isLoading || !organization}
                          styles={reactSelectStyles(isInvalid)}
                          onChange={async (selected) => {
                            if (selected && organization) {
                              setValue("item", selected);
                              if (selected.isKit) {
                                try {
                                  setIsLoadingComponents(true);
                                  const kitComponents = await organizationsApi.searchKitComponents(
                                    { limit: 100 },
                                    organization.id,
                                    selected.id,
                                  );
                                  setValue("item.kitComponents", kitComponents ?? []);
                                } catch (err) {
                                  dispatch(showErrorNotification(i18n, err));
                                } finally {
                                  setIsLoadingComponents(false);
                                }
                              }
                            }
                          }}
                        />
                      );
                    }}
                  </FormField>
                )}
              />
            )}

            {item && (
              <FormField label={i18n.t.common.code}>
                <Input value={watch("item.code")} disabled />
              </FormField>
            )}

            <FormField label={i18n.t.common.description}>
              <Input
                value={getItemDescription(
                  {
                    descriptionEn: watch("item.descriptionEn"),
                    descriptionIt: watch("item.descriptionIt"),
                    descriptionEs: watch("item.descriptionEs"),
                    descriptionFr: watch("item.descriptionFr"),
                    descriptionDe: watch("item.descriptionDe"),
                  },
                  permissions.preferredLanguage,
                )}
                disabled
              />
            </FormField>

            <FormField label={i18n.t.costObjects.singular}>
              <Input disabled value={watch("item.costObject") ?? ""} />
            </FormField>

            <FormField label={i18n.t.common.unitOfMeasure}>
              <Input disabled value={watch("item.unitOfMeasure")} />
            </FormField>

            <FormField label={i18n.t.common.cost}>
              <Input disabled value={formatPrice(watch("item.cost"), i18n.language, { max: 3, min: 3 })} />
            </FormField>

            {isLoadingComponents && <LoadingSpinner />}
            {kitComponents && !!kitComponents?.length && !isLoadingComponents && (
              <FormField label={i18n.t.common.components}>
                <Table<KitComponent>
                  className={styles.componentsTable}
                  head={
                    <tr>
                      <th>{i18n.t.common.code}</th>
                      <th>{i18n.t.common.description}</th>
                      <th>{i18n.t.common.quantity}</th>
                    </tr>
                  }
                  renderRow={(item) => (
                    <>
                      <td>{item.component.code}</td>
                      <td>{getItemDescription(item.component, i18n.language)}</td>
                      <td align="center">{item.quantity}</td>
                    </>
                  )}
                  items={kitComponents}
                />
              </FormField>
            )}
          </>
        );
      }}
    </Form>
  );
};

export default ItemForm;
